import NavigationUtils from './navigation.utils';
import CreditStatus from '../constants/credit.status';
import Base64 from './base64.utils';
import { CompleteStyles } from '../routes/CompletePage/CompletePage';
import { CompletePageSuccessType } from '../routes/CompletePage/CompletePageSuccessConfig';

export const UserIdentityStatus = {
  VALIDATED: 'VALIDATED',
  VALIDATED_PERSONALIZED_AUTO_LENDING: 'VALIDATED_PERSONALIZED_AUTO_LENDING',
  VALIDATED_WITH_AUTO_LENDING: 'VALIDATED_WITH_AUTO_LENDING',
  REJECTED: 'REJECTED',
  OPERATOR: 'OPERATOR',
  DO_OVER: 'DO_OVER',
  UPDATED: 'UPDATED',
  NEW: 'NEW',
};

export const UserApplicationStatus = {
  REJECTED: 'REJECTED',
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
};

export const UserApplicationReason = {
  INFORMATION_MISMATCH: 'INFORMATION_MISMATCH',
};

const RejectedReason = {
  UNDERAGED: 'UNDERAGED',
  OTHER: 'OTHER',
  L3: 'L3',
  BLACK_LIST: 'BLACK_LIST',
};

const DoOverReason = {
  INVALID_ID_NUMBER: 'INVALID_ID_NUMBER',
  IMAGE_QUALITY: 'IMAGE_QUALITY',
  UNMATCHING_DATA: 'UNMATCHING_DATA',
  DUPLICATE_ID: 'DUPLICATE_ID',
  NUMBER_MODIFIED: 'NUMBER_MODIFIED',
  EXPIRED_ID: 'EXPIRED_ID',
  BROKEN_ID: 'BROKEN_ID',
};

const OnboardingStatusType = {
  DUPLICATE_ID: 'duplicate-id',
  EXPIRED_ID: 'expired-id',
  UNMATCHING_DATA: 'unmatching-data',
  INVALID_ID_NUMBER: 'invalid-id-number',
  IMAGE_QUALITY: 'image-quality',
  BROKEN_ID: 'broken-id',
  NUMBER_MODIFIED: 'number-modified',
};

const OnboardingSuccessType = {
  CARD_APPROVED: 'card-approved',
  APPLICATION_REVIEW: 'application-review',
};

const OnboardingErrorType = {
  INFORMATION_MISMATCH: 'information-mismatch',
  UNDERAGED: 'underaged',
  REJECTED: 'rejected',
};

const OnboardingDoOverRedirectURL = {
  [DoOverReason.INVALID_ID_NUMBER]: `${NavigationUtils.Routes.ONBOARDING_STATUS}/${OnboardingStatusType.INVALID_ID_NUMBER}`,
  [DoOverReason.UNMATCHING_DATA]: `${NavigationUtils.Routes.ONBOARDING_STATUS}/${OnboardingStatusType.UNMATCHING_DATA}`,
  [DoOverReason.IMAGE_QUALITY]: `${NavigationUtils.Routes.ONBOARDING_STATUS}/${OnboardingStatusType.IMAGE_QUALITY}`,
  [DoOverReason.DUPLICATE_ID]: `${NavigationUtils.Routes.ONBOARDING_STATUS}/${OnboardingStatusType.DUPLICATE_ID}`,
  [DoOverReason.EXPIRED_ID]: `${NavigationUtils.Routes.ONBOARDING_STATUS}/${OnboardingStatusType.EXPIRED_ID}`,
  [DoOverReason.BROKEN_ID]: `${NavigationUtils.Routes.ONBOARDING_STATUS}/${OnboardingStatusType.BROKEN_ID}`,
};

const OnboardingRejectedRedirectURL = {
  [RejectedReason.UNDERAGED]: `${NavigationUtils.Routes.ONBOARDING_ERROR}/${OnboardingErrorType.UNDERAGED}`,
};

const OnboardingRedirectUrl = {
  [UserIdentityStatus.REJECTED]: OnboardingRejectedRedirectURL,
  [UserIdentityStatus.DO_OVER]: OnboardingDoOverRedirectURL,
};

const DefaultRedirectUrl = {
  [UserIdentityStatus.VALIDATED]: `${NavigationUtils.Routes.ONBOARDING_SUCCESS}/${OnboardingSuccessType.CARD_APPROVED}`,
  [UserIdentityStatus.VALIDATED_PERSONALIZED_AUTO_LENDING]: `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.SUCCESS}/${CompletePageSuccessType.ONBOARDING_APPROVED_AUTO_LENDING_PERSONALIZED}`,
  [UserIdentityStatus.VALIDATED_WITH_AUTO_LENDING]: `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.SUCCESS}/${CompletePageSuccessType.ONBOARDING_APPROVED_AUTO_LENDING}`,
  [UserIdentityStatus.OPERATOR]: `${NavigationUtils.Routes.ONBOARDING_SUCCESS}/${OnboardingSuccessType.APPLICATION_REVIEW}`,
  [UserIdentityStatus.REJECTED]: `${NavigationUtils.Routes.ONBOARDING_ERROR}/${OnboardingErrorType.REJECTED}`,
  [UserIdentityStatus.DO_OVER]: `${NavigationUtils.Routes.ONBOARDING_STATUS}/${OnboardingStatusType.UNMATCHING_DATA}`,
};

const CreditRedirectUrl = {
  [CreditStatus.NOT_APPLIED]: `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.SUCCESS}/${CompletePageSuccessType.DEBIT_ONBOARDING_SUCCESS}`,
};

export default class OnboardingStatusUrlUtils {
  static OnboardingStatusType = OnboardingStatusType;
  static OnboardingSuccessType = OnboardingSuccessType;
  static OnboardingErrorType = OnboardingErrorType;

  static getUrl(
    userIdentity,
    userApplication = {},
    credit = {},
    automaticLendingEnabled = false,
    personalizedCardEnabled = false,
  ) {
    // ATTENTION: This case will apply only for the users that try to pick the card of a closed organization when the
    // ATTENTION: user is not invited.
    if (
      userApplication.status === UserApplicationStatus.REJECTED &&
      userApplication.reason === UserApplicationReason.INFORMATION_MISMATCH
    ) {
      return `${NavigationUtils.Routes.ONBOARDING_ERROR}/${OnboardingErrorType.INFORMATION_MISMATCH}`;
    }

    //Checking Credit status
    const creditUrl = CreditRedirectUrl[credit?.status] || null;

    if (creditUrl) {
      return creditUrl;
    }

    //Checking User Identity status
    const redirectUrls = OnboardingRedirectUrl[userIdentity.status];

    if (!redirectUrls) {
      // ATTENTION: The following If statements are only for AML (Sanction Screening)
      if (
        userIdentity.status === UserIdentityStatus.VALIDATED &&
        userApplication.status === UserApplicationStatus.REJECTED
      ) {
        return DefaultRedirectUrl[UserIdentityStatus.REJECTED];
      }

      if (
        userIdentity.status === UserIdentityStatus.VALIDATED &&
        userApplication.status === UserApplicationStatus.PENDING
      ) {
        return DefaultRedirectUrl[UserIdentityStatus.OPERATOR];
      }

      if (
        userIdentity.status === UserIdentityStatus.VALIDATED &&
        userApplication.status === UserApplicationStatus.APPROVED &&
        personalizedCardEnabled &&
        automaticLendingEnabled
      ) {
        return DefaultRedirectUrl[
          UserIdentityStatus.VALIDATED_PERSONALIZED_AUTO_LENDING
        ];
      }
      if (
        userIdentity.status === UserIdentityStatus.VALIDATED &&
        userApplication.status === UserApplicationStatus.APPROVED &&
        automaticLendingEnabled
      ) {
        return DefaultRedirectUrl[
          UserIdentityStatus.VALIDATED_WITH_AUTO_LENDING
        ];
      }

      return (
        DefaultRedirectUrl[userIdentity.status] ||
        NavigationUtils.Routes.TRY_AGAIN
      );
    }

    const url =
      redirectUrls[userIdentity.reason] ||
      DefaultRedirectUrl[userIdentity.status];

    if (!url) {
      console.error(
        `Couldn't find the reason url for reason ${userIdentity.reason}`,
      );
      return NavigationUtils.Routes.TRY_AGAIN;
    }

    return url;
  }

  static getParams(opts) {
    const {
      credit,
      organizationCode,
      userApplication,
      automaticLendingEnabled,
    } = opts;

    let params = {};

    if (credit?.status || automaticLendingEnabled) {
      const deeplinkParameters = {
        organizationCode,
        amount: credit?.availableAmount,
      };
      const encodedParams = Base64.encode(JSON.stringify(deeplinkParameters));
      params = { params: encodedParams };
    }

    if (userApplication?.personalizedCard) {
      params.personalizedCard = true;
    }

    return params;
  }
}
